import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CartoesPage = () => {
  const { canEdit } = usePermissions();
  const canEditCartoes = canEdit('cartoes');
  const [cartoes, setCartoes] = useState([]);
  const [liderancas, setLiderancas] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    codigo: '',
    lideranca_id: '',
    link_id: '',
    status: 'ativo',
    observacoes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cartoesRes, liderancasRes, linksRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cards`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leaders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/links`, { withCredentials: true }),
      ]);
      setCartoes(cartoesRes.data);
      setLiderancas(liderancasRes.data);
      setLinks(linksRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/cards`, formData, { withCredentials: true });
      toast.success('Cartão criado com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar cartão';
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este cartão?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/cards/${id}`, { withCredentials: true });
      toast.success('Cartão excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir cartão');
    }
  };

  const resetForm = () => {
    setFormData({
      codigo: '',
      lideranca_id: '',
      link_id: '',
      status: 'ativo',
      observacoes: '',
    });
  };

  const generateCodigo = () => {
    const random = 'NFC-' + Math.random().toString(36).substring(2, 10).toUpperCase();
    setFormData({ ...formData, codigo: random });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="cartoes-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight">Cartões NFC/QR</h1>
            <p className="text-muted-foreground mt-2">Gerencie cartões vinculados a links</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-cartao-btn" disabled={!canEditCartoes}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Cartão
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cartoes.map((cartao) => {
            const lideranca = liderancas.find((l) => l.id === cartao.lideranca_id);
            const link = links.find((l) => l.id === cartao.link_id);
            return (
              <Card key={cartao.id} data-testid={`cartao-card-${cartao.id}`}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                    {cartao.codigo}
                  </CardTitle>
                  <CardDescription>
                    {lideranca?.nome_completo || 'Liderança'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Link:</span>
                    <span className="font-medium">/{link?.token || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Status:</span>
                    <span className={`font-medium capitalize ${
                      cartao.status === 'ativo' ? 'text-green-600' : 'text-muted-foreground'
                    }`}>
                      {cartao.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Acessos:</span>
                    <span className="font-bold text-primary">{cartao.total_acessos}</span>
                  </div>
                  {cartao.ultimo_acesso && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Último acesso:</span>
                      <span className="font-medium text-xs">
                        {new Date(cartao.ultimo_acesso).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleDelete(cartao.id)}
                      data-testid={`delete-cartao-btn-${cartao.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {cartoes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Nenhum cartão criado ainda.</p>
              <Button onClick={() => setDialogOpen(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Cartão
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Novo Cartão NFC/QR</DialogTitle>
              <DialogDescription>
                Crie um cartão vinculado a uma liderança e link
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="codigo">Código do Cartão</Label>
                  <div className="flex gap-2">
                    <Input
                      id="codigo"
                      value={formData.codigo}
                      onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                      placeholder="ex: NFC-12345"
                      required
                      data-testid="input-codigo"
                    />
                    <Button type="button" variant="outline" onClick={generateCodigo}>
                      Gerar
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lideranca_id">Liderança</Label>
                  <Select
                    value={formData.lideranca_id}
                    onValueChange={(value) => setFormData({ ...formData, lideranca_id: value })}
                  >
                    <SelectTrigger data-testid="select-lideranca">
                      <SelectValue placeholder="Selecione uma liderança" />
                    </SelectTrigger>
                    <SelectContent>
                      {liderancas.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.nome_completo}{l.apelido && ` (@${l.apelido})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link_id">Link</Label>
                  <Select
                    value={formData.link_id}
                    onValueChange={(value) => setFormData({ ...formData, link_id: value })}
                  >
                    <SelectTrigger data-testid="select-link">
                      <SelectValue placeholder="Selecione um link" />
                    </SelectTrigger>
                    <SelectContent>
                      {links.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          /{l.token}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="observacoes">Observações</Label>
                  <Input
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    data-testid="input-observacoes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="submit-cartao-btn">
                  Criar Cartão
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
